<template>
  <div>
    <div v-if="!isFooterOnly">
      <div class="tabs"><ul>
        <li :class="{ 'is-active': activeTab === 'general' }"><a @click="activeTab = 'general'">General</a></li>
        <li :class="{ 'is-active': activeTab === 'components' }"><a @click="activeTab = 'components'">Components</a></li>
      </ul></div>
      <camera-components v-if="activeTab === 'components'" :inventory-id="form.id" />
      <div v-show="activeTab === 'general'" class="pro-camera-details">
        <div v-if="error" class="notification is-danger is-light" role="alert">{{ error }}</div>
        <div v-if="loading" class="notification is-light" role="status">Loading device details...</div>
        <button v-if="!loaded && !loading" class="button is-small" @click="load">Retry loading</button>
        <section class="device-section">
          <h3 class="title is-6">Device</h3>
          <div class="columns">
            <div class="column">
              <label class="label" :for="`inventory-serial-${modalIndex}`">Serial number</label>
              <input :id="`inventory-serial-${modalIndex}`" class="input" :value="form.serial_number || 'Not claimed yet'" disabled />
            </div>
            <div class="column">
              <label class="label" :for="`inventory-status-${modalIndex}`">Status</label>
              <div class="select is-fullwidth"><select :id="`inventory-status-${modalIndex}`" v-model="form.camera_status" :disabled="!!modalData.key_fingerprint || busy">
                <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
              </select></div>
              <p v-if="modalData.key_fingerprint" class="help">Status follows device setup and return actions.</p>
            </div>
          </div>
          <p class="has-text-grey is-size-7">Inventory #{{ form.id }} · Camera {{ form.camera_id ? '#' + form.camera_id : 'not installed' }}</p>
        </section>
        <section class="device-section">
          <h3 class="title is-6">Ownership</h3>
          <p class="current-setting">Current owner: <strong>{{ currentOwnerLabel }}</strong></p>
          <div class="field">
            <label class="label" :for="`owner-type-${modalIndex}`">Device owner</label>
            <div class="select is-fullwidth"><select :id="`owner-type-${modalIndex}`" v-model="ownerType" :disabled="busy || restricted">
              <option value="natilon">Natilon (rental)</option><option value="company">Company (purchased)</option>
            </select></div>
          </div>
          <div v-if="ownerType === 'company'" class="field">
            <p class="label">Owner company</p>
            <searchable-select :endpoint="companyEndpoint" :disabled="busy || restricted" placeholder="Search owner company..." @select="selectOwner" />
            <p class="help">Selected: {{ ownerCompany ? ownerCompany.name : ownerCompanyId ? 'Company #' + ownerCompanyId : 'select a company from the results' }}</p>
          </div>
          <p class="help mb-3">Purchased devices can move between projects in the owner company. Rental devices cannot change projects.</p>
          <button class="button is-primary" :class="{ 'is-loading': saving === 'ownership' }" :disabled="busy || !loaded || restricted || !ownershipChanged || (ownerType === 'company' && !ownerCompanyId)" @click="saveOwnership">Save ownership</button>
        </section>
        <section class="device-section">
          <h3 class="title is-6">Allocation</h3>
          <p class="current-setting">Current allocation: <strong>{{ currentAllocationLabel }}</strong></p>
          <div class="field">
            <label class="label" :for="`allocation-type-${modalIndex}`">Who can install this device?</label>
            <div class="select is-fullwidth"><select :id="`allocation-type-${modalIndex}`" v-model="allocationType" :disabled="busy || restricted">
              <option value="none">No allocation</option><option value="company">One company only</option>
            </select></div>
          </div>
          <div v-if="allocationType === 'company'" class="field">
            <p class="label">Allocated company</p>
            <searchable-select :endpoint="companyEndpoint" :disabled="busy || restricted" placeholder="Search allocated company..." @select="selectAllocation" />
            <p class="help">Selected: {{ allocatedCompany ? allocatedCompany.name : allocatedCompanyId ? 'Company #' + allocatedCompanyId : 'select a company from the results' }}</p>
          </div>
          <p class="help mb-3">Without allocation, any eligible company with the setup code can install a Natilon-owned device. Purchased devices remain limited to their owner company.</p>
          <button class="button is-primary" :class="{ 'is-loading': saving === 'allocation' }" :disabled="busy || !loaded || restricted || !allocationChanged || (allocationType === 'company' && !allocatedCompanyId)" @click="saveAllocation">Save allocation</button>
          <p v-if="restricted" class="help has-text-warning-dark mt-3">Ownership and allocation cannot change while the device is revoked or awaiting return inspection.</p>
        </section>
      </div>
    </div>
    <div v-else class="buttons">
      <button class="button" :disabled="isSaving" @click="closeTopModal">Close</button>
      <button v-if="activeTab === 'general' && !modalData.key_fingerprint" class="button is-success" :class="{ 'is-loading': isSaving }" :disabled="isSaving" @click="$root.$emit(`pro-camera-inventory-save-${modalIndex}`)">Save status</button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { httpInstance as http } from "@/plugins/http";
import endpoints from "@/api/endpoints";
import toast from "@/utils/toast";
import CameraComponents from "@/components/CameraComponents.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";

export default {
  name: "ModalProCameraInventory",
  components: { CameraComponents, SearchableSelect },
  props: {
    modalData: { type: Object, default: () => ({}) },
    modalIndex: { type: Number, required: true },
    isFooterOnly: { type: Boolean, default: false },
  },
  data: () => ({
    form: { id: null, serial_number: "", camera_status: "pending", camera_id: null },
    ownerType: "natilon", ownerCompanyId: null, ownerCompany: null,
    allocationType: "none", allocatedCompanyId: null, allocatedCompany: null,
    error: "", loading: false, loaded: false, saving: "",
    companyEndpoint: endpoints.COMPANIES,
    statuses: ["unclaimed", "stock", "assigned", "returning", "created", "pending", "claimed", "active", "disabled", "lost", "retired"],
  }),
  computed: {
    ...mapState(["isSaving"]),
    activeTab: {
      get() { return this.$store.state.modalStack[this.modalIndex]?.activeTabIndex === 1 ? "components" : "general"; },
      set(value) { this.$store.dispatch("updateModalTab", value === "components" ? 1 : 0); },
    },
    currentOwnership() { return (this.modalData.ownership || []).find(item => item.ended_at == null) || this.modalData.ownership?.[0]; },
    currentOwnerId() { return this.currentOwnership?.owner_company_id ?? null; },
    currentOwnerLabel() { return !this.loaded ? "Not loaded" : this.currentOwnerId ? `${this.ownerCompany?.id === this.currentOwnerId ? this.ownerCompany.name : 'Company #' + this.currentOwnerId} (purchased)` : "Natilon (rental)"; },
    currentAllocationLabel() { return !this.loaded ? "Not loaded" : this.modalData.allocated_company_id ? (this.allocatedCompany?.id === this.modalData.allocated_company_id ? this.allocatedCompany.name : `Company #${this.modalData.allocated_company_id}`) : "None"; },
    ownershipChanged() { return (this.ownerType === "company" ? this.ownerCompanyId : null) !== this.currentOwnerId; },
    allocationChanged() { return (this.allocationType === "company" ? this.allocatedCompanyId : null) !== (this.modalData.allocated_company_id ?? null); },
    busy() { return this.loading || this.isSaving; },
    restricted() { return !!this.modalData.revoked_at || this.modalData.camera_status === "returning"; },
  },
  watch: {
    modalData: { immediate: true, handler(value) {
      if (!value) return;
      this.form = { id: value.id, serial_number: value.serial_number || "", camera_status: value.camera_status || "pending", camera_id: value.camera_id || null };
      const ownership = (value.ownership || []).find(item => item.ended_at == null) || value.ownership?.[0];
      if (this.saving !== "allocation") {
        this.ownerCompanyId = ownership?.owner_company_id ?? null;
        this.ownerType = this.ownerCompanyId ? "company" : "natilon";
      }
      if (this.saving !== "ownership") {
        this.allocatedCompanyId = value.allocated_company_id ?? null;
        this.allocationType = this.allocatedCompanyId ? "company" : "none";
      }
    } },
  },
  methods: {
    ...mapActions(["closeTopModal", "fetchList"]),
    context() { return { id: this.form.id, modalId: this.$store.state.modalStack[this.modalIndex]?.id }; },
    selectOwner(company) { this.ownerCompany = company; this.ownerCompanyId = company?.id ?? null; },
    selectAllocation(company) { this.allocatedCompany = company; this.allocatedCompanyId = company?.id ?? null; },
    async load() {
      this.loading = true; this.error = ""; this.loaded = false;
      try { await this.$store.dispatch("loadProCameraInventory", this.context()); this.loaded = true; }
      catch (error) { this.error = error.message || "Could not load device details."; }
      finally { this.loading = false; }
    },
    saveOwnership() { return this.saveSetting("ownership"); },
    saveAllocation() { return this.saveSetting("allocation"); },
    async saveSetting(type) {
      if (this.busy || !this.loaded || this.restricted) return;
      this.error = ""; this.saving = type;
      this.$store.commit("SET_IS_SAVING", true);
      try {
        if (type === "ownership") {
          if (this.ownerType === "company" && !this.ownerCompanyId) throw new Error("Select the owner company.");
          await this.$store.dispatch("saveProCameraOwnership", { ...this.context(), ownerCompanyId: this.ownerType === "company" ? this.ownerCompanyId : null });
        } else {
          if (this.allocationType === "company" && !this.allocatedCompanyId) throw new Error("Select the allocated company.");
          await this.$store.dispatch("saveProCameraAllocation", { ...this.context(), companyId: this.allocationType === "company" ? this.allocatedCompanyId : null });
        }
        toast.success(type === "ownership" ? "Ownership saved" : "Allocation saved");
        this.fetchList();
      } catch (error) { this.error = error.response?.data?.message || error.message || "Could not save device settings."; }
      finally { this.saving = ""; this.$store.commit("SET_IS_SAVING", false); }
    },
    async handleSave() {
      if (this.busy || !this.loaded) return;
      this.$store.commit("SET_IS_SAVING", true); this.error = "";
      try {
        const res = await http.patch(endpoints.PRO_CAMERA_INVENTORIES, { id: this.form.id, status: this.form.camera_status });
        if (!res || res.status > 299) throw new Error(res?.data?.message || "Failed to update inventory");
        toast.success("Inventory updated"); this.closeTopModal(); this.fetchList();
      } catch (error) { this.error = error.response?.data?.message || error.message; }
      finally { this.$store.commit("SET_IS_SAVING", false); }
    },
  },
  mounted() {
    if (!this.isFooterOnly) { this.$root.$on(`pro-camera-inventory-save-${this.modalIndex}`, this.handleSave); this.load(); }
  },
  beforeDestroy() {
    if (!this.isFooterOnly) this.$root.$off(`pro-camera-inventory-save-${this.modalIndex}`, this.handleSave);
  },
};
</script>

<style scoped>
.buttons { justify-content: flex-end; }
.device-section + .device-section { border-top: 1px solid #eee; padding-top: 1.5rem; margin-top: 1.5rem; }
.current-setting { margin-bottom: 1rem; }
.device-section .help { max-width: 68ch; }
</style>
